import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { DateTime } from 'luxon';
import { ParticipantRole } from '@getaccept/lib-shared/src/dealroom/types/enums/participant-role';
import { DealroomEventType, ParticipantCreationSource } from '../../api/timeline/types/log.types';
import type {
  ActionPlanUpdatedEventLogData,
  ContextualCommentUpdatedEventLogData,
  EventLogData,
  FeatureSettingsUpdatedEventLogData,
  Log,
  ParticipantCreatedEventLogData,
  PublishedEventLogData,
  TaskUpdatedEventLogData,
  VisitedEventLogData,
} from '../../api/timeline/types/log.types';
import { CommentStatus } from '../../contextual-commenting/types/comment-status';
import { TaskStatus } from '../../mutual-action-plan/types/enums/task-status';
import type { TimelineEventMetadata } from '../types/timeline.types';

type ExcludedEvents =
  | DealroomEventType.MilestoneCreated
  | DealroomEventType.MilestoneDeleted
  | DealroomEventType.TaskCreated
  | DealroomEventType.TaskDeleted;

const settingsToggle = 'settings-toggle';

export default class TimelineHelper {
  private static formatDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static eventTypeToIconAndName: Record<
    Exclude<DealroomEventType, ExcludedEvents>,
    (data: EventLogData) => TimelineEventMetadata
  > = {
    [DealroomEventType.ActionPlanUpdated]: (data: ActionPlanUpdatedEventLogData) => {
      if (data.updateActionPlan?.visible == null) return null;
      return data.updateActionPlan.visible
        ? { icon: 'clipboard', name: t('dr_published_action_plan') }
        : { icon: 'clipboard-undo', name: t('dr_unpublished_action_plan') };
    },

    [DealroomEventType.TaskUpdated]: (data: TaskUpdatedEventLogData) =>
      data.updateTask?.status === TaskStatus.Completed
        ? { icon: 'clipboard-check', name: t('dr_completed_a_task') }
        : null,

    [DealroomEventType.PostCreated]: () => ({ icon: 'megaphone', name: t('dr_created_a_post') }),

    [DealroomEventType.Published]: (data: PublishedEventLogData) =>
      data.version === 1 || data.__typename === 'DealroomEntityAdminPublishedEventLogData'
        ? { icon: 'publish', name: t('dr_published_the_room') }
        : { icon: 'publish', name: t('dr_published_content_edits') },

    [DealroomEventType.Unpublished]: () => ({
      icon: 'arrow-bend-down-left',
      name: t('dr_unpublished_the_room'),
    }),

    [DealroomEventType.Deleted]: () => ({ icon: 'delete-bin', name: t('dr_deleted_the_room') }),

    [DealroomEventType.Visited]: (data: VisitedEventLogData) =>
      data.isFirstVisit
        ? { icon: 'view', name: t('dr_visited_for_the_first_time') }
        : { icon: 'view', name: t('dr_visited_the_room') },

    [DealroomEventType.AccessRuleUpdated]: () => ({
      icon: settingsToggle,
      name: t('dr_changed_room_access'),
    }),

    [DealroomEventType.FeatureSettingsUpdated]: (data: FeatureSettingsUpdatedEventLogData) => {
      const isFeatureEnabled = Object.values(data.updateFeatureSettings).some(
        setting => setting === false
      );
      return isFeatureEnabled
        ? { icon: settingsToggle, name: t('dr_enabled_a_room_feature') }
        : { icon: settingsToggle, name: t('dr_disabled_a_room_feature') };
    },

    [DealroomEventType.FileUploaded]: () => ({ icon: 'upload', name: t('dr_uploaded_a_file') }),

    [DealroomEventType.FileDownloaded]: () => ({
      icon: 'download',
      name: t('dr_downloaded_a_file'),
    }),

    [DealroomEventType.FileDeleted]: () => ({ icon: 'delete-bin', name: t('dr_deleted_a_file') }),

    [DealroomEventType.ParticipantCreated]: ({
      creationSource,
      createdParticipantData,
    }: ParticipantCreatedEventLogData) => {
      const isCollaborator = createdParticipantData?.role === ParticipantRole.Collaborator;

      switch (creationSource) {
        case ParticipantCreationSource.SenderSite:
          return {
            icon: 'user-add',
            name: t(isCollaborator ? 'dr_added_a_collaborator' : 'dr_added_a_participant'),
          };
        case ParticipantCreationSource.ParticipantSiteInvite:
          return { icon: 'mail-sign-at', name: t('dr_invited_a_participant_via_email') };
        case ParticipantCreationSource.UniversalLink:
          return { icon: 'globe', name: t('dr_joined_via_universal_url') };
        // TODO: Check with design
        case ParticipantCreationSource.API:
          return {
            icon: 'user-add',
            name: t(
              isCollaborator ? 'dr_added_a_collaborator_via_api' : 'dr_added_a_participant_via_api'
            ),
          };
        default:
          return null;
      }
    },

    [DealroomEventType.ParticipantDeleted]: () => ({
      icon: 'user-remove',
      name: t('dr_removed_a_participant'),
    }),

    [DealroomEventType.ConversationMessageCreated]: () => ({
      icon: 'conversation',
      name: t('dr_wrote_a_chat_message'),
    }),

    [DealroomEventType.ContextualCommentReplyCreated]: () => ({
      icon: 'comments',
      name: t('dr_replied_in_a_thread'),
    }),

    [DealroomEventType.ContextualCommentUpdated]: ({
      status,
      updatedData,
    }: ContextualCommentUpdatedEventLogData) => {
      switch (updatedData?.status) {
        case CommentStatus.Resolved:
          return { icon: 'bold-check-mark', name: t('dr_resolved_a_comment') };
        case CommentStatus.Active:
          if (status === CommentStatus.Draft) {
            return {
              icon: 'comments',
              name: t('dr_created_a_new_comment'),
            };
          }
          if (status === CommentStatus.Resolved) {
            return {
              icon: 'comments',
              name: t('dr_reopened_a_comment'),
            };
          }
          return null;
        default:
          return null;
      }
    },

    [DealroomEventType.RoomOwnerUpdated]: () => ({
      icon: 'user-move',
      name: t('dr_changed_room_owner'),
    }),
  };

  static organizeLogsByDate(logs: Log[]): { date: string; logs: Log[] }[] {
    const filteredEvents = logs.filter(
      log => this.eventTypeToIconAndName[log.data.type] !== undefined
    );

    return filteredEvents.reduce((acc, log) => {
      const createdAt = new Date(log.createdAt);
      const dateKey = this.formatDate(createdAt);

      const index = acc.findIndex(item => item.date === dateKey);
      if (index === -1) {
        acc.push({ date: dateKey, logs: [log] });
      } else {
        acc[index].logs.push(log);
      }
      return acc;
    }, []);
  }

  static getTimelineEventMetadata(log: Log): TimelineEventMetadata | null {
    const getIconAndName = this.eventTypeToIconAndName[log.data.type];
    return getIconAndName ? getIconAndName(log.data) : null;
  }

  static calculateTimeInterval(fromDate: Date, toDate: Date) {
    const fromMillis = DateTime.fromJSDate(fromDate).startOf('day').toMillis();
    const toMillis = DateTime.fromJSDate(toDate).endOf('day').toMillis();
    return `${fromMillis},${toMillis}`;
  }
}
